// Økt-lista: lesing, gruppering og summering.
//
// SUMMERING GÅR ALLTID GJENNOM `sumTimer` FRA @skade/dugnad. Den runder ÉN gang
// til slutt. Runder man per rad og summerer etterpå, drifter regnskapet: 12
// økter à 20 minutter blir 12 × 0,5 t = 6 t i stedet for 4 t. Det er ikke en
// kosmetisk feil — timene er grunnlaget for et frivillighetsregnskap.
// Kontrakten sier det rett ut under «Hva klienten IKKE skal gjøre».
import { sumTimer, timerTekst, AKTIVITET_NAVN, type Aktivitetstype, type Status } from '@skade/dugnad';
import { somDatoFelt, TIDSSONE } from './tid.js';

/**
 * Én rad fra `GET /okter`. Andres økter kommer gjennom `offentligOkt()` og har
 * derfor ikke `hva` — det er ikke en mangel, det er personvernet i pakken.
 */
export interface ØktRad {
  id: string;
  personId: string;
  type: Aktivitetstype;
  startTsMs: number;
  minutter: number;
  kilde: 'git' | 'manuell' | 'import';
  status: Status;
  hva?: string;
  bolge?: string;
  oppdragskode?: string;
  attestertAv?: string;
  attestertTsMs?: number;
  /** Tjenestens merke på egne rader. */
  egen: boolean;
}

export const STATUS_NAVN: Record<Status, string> = {
  utkast: 'Utkast',
  meldt: 'Meldt',
  godkjent: 'Godkjent',
  avvist: 'Avvist',
};

export const KILDE_NAVN: Record<ØktRad['kilde'], string> = {
  git: 'Fra git',
  manuell: 'Meldt manuelt',
  import: 'Importert',
};

const TYPER = Object.keys(AKTIVITET_NAVN) as Aktivitetstype[];
const STATUSER = Object.keys(STATUS_NAVN) as Status[];

function tekst(v: unknown): string | undefined {
  return typeof v === 'string' && v ? v : undefined;
}

/**
 * Les én rad. Ukjente typer/statuser/kilder faller til det mest nøytrale
 * alternativet framfor å vises ordrett — en rad merket «annet» er ærligere enn
 * en rad merket med en verdi klienten ikke forstår.
 */
export function lesØkt(rå: unknown): ØktRad | null {
  if (!rå || typeof rå !== 'object') return null;
  const r = rå as Record<string, unknown>;
  const id = tekst(r.id);
  if (!id) return null;
  if (typeof r.startTsMs !== 'number' || !Number.isFinite(r.startTsMs)) return null;
  if (typeof r.minutter !== 'number' || !Number.isFinite(r.minutter)) return null;
  const type = TYPER.includes(r.type as Aktivitetstype) ? (r.type as Aktivitetstype) : 'annet';
  const status = STATUSER.includes(r.status as Status) ? (r.status as Status) : 'meldt';
  const kilde = r.kilde === 'git' || r.kilde === 'import' ? r.kilde : 'manuell';
  return {
    id,
    personId: tekst(r.personId) ?? '(ukjent)',
    type,
    startTsMs: r.startTsMs,
    minutter: r.minutter,
    kilde,
    status,
    hva: tekst(r.hva),
    bolge: tekst(r.bolge),
    oppdragskode: tekst(r.oppdragskode),
    attestertAv: tekst(r.attestertAv),
    attestertTsMs: typeof r.attestertTsMs === 'number' ? r.attestertTsMs : undefined,
    // `_egen` er tjenestens merke. Understreken er dens navnevalg, ikke vårt, så
    // den oversettes her og forsvinner ut av resten av klienten.
    egen: r._egen === true,
  };
}

export function lesØkter(rå: unknown): ØktRad[] {
  const liste = (rå && typeof rå === 'object' ? (rå as Record<string, unknown>).okter : null);
  if (!Array.isArray(liste)) return [];
  return liste.map(lesØkt).filter((o): o is ØktRad => o !== null);
}

/** Sum i timer — én avrunding, til slutt. */
export function sumTimerFor(økter: readonly ØktRad[]): number {
  return sumTimer(økter.map((o) => o.minutter));
}

/** Sum som «7 t 30 min». Regnes av minuttene, ikke av det avrundede timetallet. */
export function sumTekstFor(økter: readonly ØktRad[]): string {
  return timerTekst(økter.reduce((s, o) => s + o.minutter, 0));
}

export interface Dagsgruppe {
  /** «2026-07-15» i norsk tid. */
  dato: string;
  /** Første økt i gruppa — til datooverskriften. */
  tsMs: number;
  økter: ØktRad[];
  minutter: number;
  timer: number;
}

/**
 * Grupper økter per NORSK dato, nyeste dag først og nyeste økt først innen dagen.
 *
 * Dagsgrensa er norsk midnatt, ikke UTC-midnatt. Ellers ville en økt meldt kl.
 * 01:00 om sommeren havnet under gårsdagen i lista — datoen i skjemaet og datoen
 * i lista hadde sagt ulike ting om samme økt.
 */
export function grupperPerDag(økter: readonly ØktRad[], sone: string = TIDSSONE): Dagsgruppe[] {
  const per = new Map<string, ØktRad[]>();
  for (const o of økter) {
    const d = somDatoFelt(o.startTsMs, sone);
    const l = per.get(d);
    if (l) l.push(o); else per.set(d, [o]);
  }
  return [...per.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([dato, liste]) => {
      const sortert = [...liste].sort((a, b) => b.startTsMs - a.startTsMs || a.id.localeCompare(b.id, 'nb'));
      const minutter = sortert.reduce((s, o) => s + o.minutter, 0);
      return { dato, tsMs: sortert[0].startTsMs, økter: sortert, minutter, timer: sumTimerFor(sortert) };
    });
}

/** Kan JEG endre denne økta? Speiler `PATCH /okter/:id`: bare eier, bare utkast/meldt. */
export function kanEndre(økt: ØktRad): boolean {
  return økt.egen && (økt.status === 'utkast' || økt.status === 'meldt');
}

/**
 * Kan JEG attestere denne økta?
 *
 * Speiler `POST /okter/:id/attester`: krever attesteringsrett, at økta er
 * «meldt», og at den IKKE er min egen. Selvattestering er hele grunnen til at
 * attestering finnes — en knapp som tilbyr den ville lært brukeren feil regel.
 */
export function kanAttestere(økt: ØktRad, jegKanAttestere: boolean): boolean {
  return jegKanAttestere && !økt.egen && økt.status === 'meldt';
}

export interface ØktFilter {
  type?: Aktivitetstype;
  status?: Status;
  /** 'egne' | 'andres' | undefined (alle) */
  eier?: 'egne' | 'andres';
}

export function filtrerØkter(økter: readonly ØktRad[], f: ØktFilter): ØktRad[] {
  return økter.filter((o) => {
    if (f.type && o.type !== f.type) return false;
    if (f.status && o.status !== f.status) return false;
    if (f.eier === 'egne' && !o.egen) return false;
    if (f.eier === 'andres' && o.egen) return false;
    return true;
  });
}

/** Økter som venter på at NOEN andre attesterer dem. */
export function tilAttestering(økter: readonly ØktRad[], jegKanAttestere: boolean): ØktRad[] {
  return økter.filter((o) => kanAttestere(o, jegKanAttestere))
    .sort((a, b) => a.startTsMs - b.startTsMs);
}
